import React, { useMemo, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Trash2, ArrowUpDown, ArrowUp, ArrowDown, Undo2, AlertCircle, RotateCcw } from 'lucide-react';
import { formatCurrency } from '../../lib/formatters';

// 10/27 (Josh: "i dont really like the relink button … too detailed"): hidden, not removed.
const SHOW_RELINK = false;
const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  const date = new Date(dateStr);
  return `${String(date.getUTCMonth() + 1).padStart(2, '0')}/${String(date.getUTCDate()).padStart(2, '0')}/${date.getUTCFullYear()}`;
};

const TYPE_LABELS = {
  PAYMENT: 'Payment', ACH_RETURN: 'ACH Return', BOUNCE_FEE: 'Bounce Fee',
  REFUND: 'Refund', LAWYER_COLLECTION: 'Attorney Collection', LAWYER_FEE: 'Lawyer Fee',
  DEFAULT_FEE: 'Default Fee', DEFERRED_FEE: 'Deferred Fee', CUSTOM_FEE: 'Custom Fee', WRITE_OFF: 'Write-Off', REFINANCE_PAYOFF: 'Refinance Payoff',
  EARLY_PAYOFF: 'Early Payoff',
  DISCOUNT: 'Discount', FEE_DISCOUNT: 'Fee Discount', ADJUSTMENT_INCREASE: 'Adjustment',
  'ACH': 'Payment', 'Wire': 'Payment', 'Zelle': 'Payment', 'Manual': 'Payment',
  'Check': 'Payment', 'Cash': 'Payment', 'Bounce': 'ACH Return', 'Fee': 'Default Fee',
  'Fees': 'Default Fee', 'Discount': 'Discount', 'Refinance Payoff': 'Refinance Payoff',
  'Early Payoff': 'Early Payoff',
  'Lawyer Collection': 'Attorney Collection', 'Adjustment': 'Adjustment',
  'Manual Adjustment': 'Manual Adjustment', 'MANUAL_ADJUSTMENT': 'Manual Adjustment',
};
const typeLabel = (type) => TYPE_LABELS[type] || type;

// 08/20 (Josh): "if i wanted to only see zelles i can select that filter to
// view only zelle payments". Manual payments carry the method in the
// reference ("Zelle-08-15-2026"), description, or notes; bank-feed uploads
// have raw ACH trace references and count as ACH.
export const detectPaymentMethod = (t) => {
  const s = `${t.reference || t.reference_number || ''} ${t.description || ''} ${t.notes || ''}`.toLowerCase();
  if (s.includes('zelle')) return 'Zelle';
  if (s.includes('venmo')) return 'Venmo';
  if (s.includes('wire')) return 'Wire';
  if (s.includes('check')) return 'Check';
  if (s.includes('cash app') || s.includes('cashapp')) return 'Cash App';
  if (s.includes('cash')) return 'Cash';
  if (s.includes('credit')) return 'Credit Card';
  return 'ACH';
};

const SortIcon = ({ sortConfig, column }) => {
  if (sortConfig.key !== column) return <ArrowUpDown className="h-4 w-4 ml-1 opacity-50" />;
  return sortConfig.direction === 'asc'
    ? <ArrowUp className="h-4 w-4 ml-1" />
    : <ArrowDown className="h-4 w-4 ml-1" />;
};

const TransactionList = ({
  transactions,
  deal,
  selectedTransactions,
  typeFilter,
  methodFilter,
  isAdmin,
  onToggleSelection,
  onRangeSelect,
  onToggleSelectAll,
  onEditTransaction,
  onDeleteTransaction,
  onMarkBounced,
  onRefundTransaction,
  onBounceTransaction,
  onRelinkReturn,
  onReverseDefer,
}) => {
  const [sortConfig, setSortConfig] = useState({ key: 'date', direction: 'asc' });
  const [bounceConfirmId, setBounceConfirmId] = useState(null);
  // 10/14 (Josh, EVER 10941): a pull that already has a return must not offer "Bounce" again —
  // the API refuses it anyway (409). A return points at its pull via linked_transaction_id /
  // metadata.bounced_transaction_id; the pull may also carry metadata.ach_return_id.
  const returnByPaymentId = useMemo(() => {
    const m = new Map();
    (transactions || []).forEach((t) => {
      if (t.type !== 'ACH_RETURN' || t.voided || t.status === 'void') return;
      const pid = t.linked_transaction_id || t.metadata?.bounced_transaction_id;
      if (pid) m.set(pid, t);
    });
    return m;
  }, [transactions]);
  // 10/27 (Josh: "if a bounce is deleted … the option for bouncing should come back"): a pull also counts as
  // returned when its own metadata.ach_return_id points at a return that is still LIVE on this ledger; a dangling
  // id (the return was deleted) never hides the Bounce button again.
  const liveReturnIds = useMemo(() => new Set((transactions || [])
    .filter((t) => t.type === 'ACH_RETURN' && !t.voided && t.status !== 'void').map((t) => t.id)), [transactions]);
  // 10/27 (Josh: 'say "Bounced - 41d4f209a7", this way when looking at the deal I know that one bounced'): the
  // pull each live return reverses, by return id, so the return's note can name it.
  const paymentById = useMemo(() => {
    const m = new Map();
    (transactions || []).forEach((t) => { if (t.type === 'PAYMENT') m.set(t.id, t); });
    return m;
  }, [transactions]);
  const pullOfReturn = (ret) => {
    const pid = ret.linked_transaction_id || ret.metadata?.bounced_transaction_id;
    if (pid && paymentById.has(pid)) return paymentById.get(pid);
    return (transactions || []).find((p) => p.type === 'PAYMENT' && p.metadata?.ach_return_id === ret.id) || null;
  };
  const pullLabel = (p) => p.reference_number || p.reference || p.id.slice(0, 10);
  const returnedPaymentIds = useMemo(() => {
    const s = new Set(returnByPaymentId.keys());
    (transactions || []).forEach((p) => {
      if (p.type === 'PAYMENT' && p.metadata?.ach_return_id && liveReturnIds.has(p.metadata.ach_return_id)) s.add(p.id);
    });
    return s;
  }, [returnByPaymentId, transactions, liveReturnIds]);
  // 10/22 (Josh, 10754: "give me the ability"): an ACH Return can be moved onto the pull it really
  // reverses — pick among same-amount live pulls that carry no return yet (±14 days).
  const [relinkFor, setRelinkFor] = useState(null);      // return id being re-linked
  const [relinkTo, setRelinkTo] = useState('');
  const relinkCandidates = (ret) => {
    const amt = Math.abs(Number(ret.amount) || 0);
    const rd = new Date(String(ret.date).slice(0, 10)).getTime();
    return (transactions || [])
      .filter((p) => p.type === 'PAYMENT' && p.status !== 'void' && p.status !== 'bounced'
        && Math.abs(Number(p.amount) || 0) === amt && !returnedPaymentIds.has(p.id)
        && Math.abs(new Date(String(p.date).slice(0, 10)).getTime() - rd) <= 14 * 86400000)
      .sort((a, b) => String(a.date).localeCompare(String(b.date)));
  };

  // Shift-click range selection (Gmail/Finder pattern).
  // anchorIdRef: id of the most recently clicked checkbox (with or without
  //   shift). The "anchor" for the next shift-click range.
  // shiftHeldRef: captured at mousedown — `e.shiftKey` is reliable on mousedown
  //   even when the framework click event has already toggled checked (and
  //   even when the click is dispatched programmatically by tooling).
  const anchorIdRef = useRef(null);
  const shiftHeldRef = useRef(false);

  const handleCheckboxChange = (transactionId, visibleIds) => {
    const wasChecked = selectedTransactions.includes(transactionId);
    const targetChecked = !wasChecked;
    const shiftHeld = shiftHeldRef.current;
    shiftHeldRef.current = false;  // reset for next click

    const anchorId = anchorIdRef.current;
    const anchorIdx = anchorId ? visibleIds.indexOf(anchorId) : -1;
    const clickedIdx = visibleIds.indexOf(transactionId);

    // Shift+click range selection. Falls back to single-row toggle when:
    //   • shift wasn't actually held, OR
    //   • no anchor exists (first click of the session), OR
    //   • the anchor is no longer in the visible list (filter/sort dropped
    //     it — reset anchor and treat as single click), OR
    //   • parent didn't wire the range setter prop.
    if (shiftHeld && anchorId && anchorIdx >= 0 && clickedIdx >= 0 && onRangeSelect) {
      const [lo, hi] = anchorIdx <= clickedIdx ? [anchorIdx, clickedIdx] : [clickedIdx, anchorIdx];
      const rangeIds = visibleIds.slice(lo, hi + 1);
      // The clicked row's checked state was already toggled by onChange firing
      // (we're INSIDE the onChange handler). targetChecked is the post-toggle
      // state. Apply that uniformly to every id in the range — including the
      // clicked row, which onRangeSelect will overwrite to the same state it
      // already has, so no double-toggle.
      onRangeSelect(rangeIds, targetChecked);
      anchorIdRef.current = transactionId;
      return;
    }

    onToggleSelection(transactionId);
    anchorIdRef.current = transactionId;
  };

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const getSortedTransactions = () => {
    // Voided rows are functionally "deleted" (e.g. a refi-payoff whose link
    // was broken via the system-wide unlink rule). They stay in the DB for
    // audit, but should NOT clutter the operator's working ledger view.
    let filtered = transactions.filter(t => t.status !== 'void');
    if (typeFilter === 'payments') filtered = filtered.filter(t => t.type === 'PAYMENT' && t.cleared_payment_eligible === true);
    else if (typeFilter === 'bounces') filtered = filtered.filter(t => t.type === 'ACH_RETURN');
    else if (typeFilter === 'fees') filtered = filtered.filter(t => ['BOUNCE_FEE', 'DEFAULT_FEE', 'DEFERRED_FEE', 'CUSTOM_FEE'].includes(t.type));
    else if (typeFilter === 'early_payoffs') filtered = filtered.filter(t => t.type === 'EARLY_PAYOFF');
    else if (typeFilter === 'refi_payoffs') filtered = filtered.filter(t => t.type === 'REFINANCE_PAYOFF');
    // Payment-method filter (Zelle, Wire, ...) — payments only by definition.
    if (methodFilter) filtered = filtered.filter(t => t.type === 'PAYMENT' && detectPaymentMethod(t) === methodFilter);
    // Same-date tiebreaker: keep PAYMENT-class rows above the ACH_RETURN that
    // reverses them and the BOUNCE_FEE/DEFAULT_FEE that follow. Matches the
    // backend's running-balance walk in routes/transactions.py.
    const typeOrder = {
      PAYMENT: 0, REFUND: 0, DISCOUNT: 0, ADJUSTMENT_INCREASE: 0,
      LAWYER_COLLECTION: 0, WRITE_OFF: 0, REFINANCE_PAYOFF: 0, EARLY_PAYOFF: 0,
      ACH_RETURN: 1,
      BOUNCE_FEE: 2, DEFAULT_FEE: 2, DEFERRED_FEE: 2, CUSTOM_FEE: 2, FEE_DISCOUNT: 2,
    };
    const tieBreak = (a, b) =>
      (typeOrder[a.type] ?? 9) - (typeOrder[b.type] ?? 9) ||
      String(a.created_at || '').localeCompare(String(b.created_at || ''));
    const sorted = filtered.sort((a, b) => {
      if (sortConfig.key === 'date') return (new Date(a.date) - new Date(b.date)) || tieBreak(a, b);
      if (sortConfig.key === 'amount') return (a.amount - b.amount) || tieBreak(a, b);
      if (sortConfig.key === 'type') return a.type.localeCompare(b.type) || tieBreak(a, b);
      return tieBreak(a, b);
    });
    return sortConfig.direction === 'desc' ? sorted.reverse() : sorted;
  };

  const sortedVisible = getSortedTransactions();
  const visibleIds = sortedVisible.map(t => t.id);
  const allVisibleSelected = visibleIds.length > 0 && visibleIds.every(id => selectedTransactions.includes(id));

  // Timeline dividers:
  //   • "Default declared" — anchored on deal.defaulted_at (the deal-level event
  //     marker stamped by /api/default-recovery/{id}/mark-default). NEVER on a
  //     transaction date — that broke when a CUSTOM_FEE was miscategorized as
  //     DEFAULT_FEE and dragged the banner to the wrong spot.
  //   • "Legal collections begin" — first LAWYER_COLLECTION transaction (its
  //     existence on the ledger IS the event).
  // Dividers are hidden whenever a type filter is active.
  const dividerByAnchorId = {};
  const standaloneDividers = []; // dividers not tied to a transaction row
  if (!typeFilter && !methodFilter) {
    const dateAsc = [...transactions].sort((a, b) => new Date(a.date) - new Date(b.date));
    const firstLawyerCollection = dateAsc.find(t => t.type === 'LAWYER_COLLECTION');

    // "Default declared" — driven by deal.defaulted_at exclusively.
    if (deal?.defaulted_at) {
      const declaredDate = new Date(deal.defaulted_at);
      // Anchor to the first transaction whose date is >= defaulted_at (so the
      // banner renders in chronological order). If none exist after the date,
      // fall back to a standalone trailing divider.
      const anchor = dateAsc.find(t => new Date(t.date) >= declaredDate);
      const label = `Default declared — ${formatDate(deal.defaulted_at)}`;
      if (anchor) {
        dividerByAnchorId[anchor.id] = { label, tone: 'amber' };
      } else {
        standaloneDividers.push({ key: 'default-declared', label, tone: 'amber' });
      }
    }

    if (firstLawyerCollection) {
      dividerByAnchorId[firstLawyerCollection.id] = {
        label: `Legal collections begin — ${formatDate(firstLawyerCollection.date)}`,
        tone: 'blue',
      };
    }

    // "In-house recovery begins" — first cleared PAYMENT dated on/after the
    // default (09/23: in-house money counts as recovery; cutoff = defaulted_at).
    if (deal?.defaulted_at) {
      const cutoff = String(deal.defaulted_at).slice(0, 10);
      const firstInHouse = dateAsc.find(t =>
        (t.type === 'PAYMENT' || t.type === 'EARLY_PAYOFF') &&
        ['successful', 'cleared', 'success'].includes((t.status || '').toLowerCase()) &&
        String(t.date).slice(0, 10) >= cutoff);
      if (firstInHouse && !dividerByAnchorId[firstInHouse.id]) {
        dividerByAnchorId[firstInHouse.id] = {
          label: `In-house recovery begins — ${formatDate(firstInHouse.date)}`,
          tone: 'blue',
        };
      }
    }

    // "Settlement agreed" — the proposed settlement's agreement date.
    const psDate = deal?.proposed_settlement?.date;
    if (psDate) {
      const agreed = new Date(psDate);
      const anchor = dateAsc.find(t => new Date(t.date) >= agreed && !dividerByAnchorId[t.id]);
      const label = `Settlement agreed — ${formatDate(psDate)} · ${Number(deal.proposed_settlement.amount || 0).toLocaleString('en-US', { style: 'currency', currency: 'USD' })}`;
      if (anchor) {
        dividerByAnchorId[anchor.id] = { label, tone: 'amber' };
      } else {
        standaloneDividers.push({ key: 'settlement-agreed', label, tone: 'amber' });
      }
    }
  }

  if (transactions.length === 0) {
    return <div className="text-center py-12 text-gray-500">No transactions found for this deal.</div>;
  }

  return (
    <div className="overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow className="bg-blue-100">
            <TableHead className="w-12">
              <input
                type="checkbox"
                checked={allVisibleSelected}
                onChange={() => onToggleSelectAll(visibleIds, allVisibleSelected)}
                className="h-4 w-4 cursor-pointer"
                title={typeFilter ? `Select all ${visibleIds.length} filtered rows` : 'Select all rows'}
                data-testid="select-all-checkbox"
              />
            </TableHead>
            {['type', 'date', 'amount'].map(col => (
              <TableHead key={col} className="cursor-pointer hover:bg-blue-200 select-none" onClick={() => handleSort(col)}>
                <div className="flex items-center">
                  {col.charAt(0).toUpperCase() + col.slice(1)}
                  <SortIcon sortConfig={sortConfig} column={col} />
                </div>
              </TableHead>
            ))}
            <TableHead>Reference</TableHead>
            <TableHead>Running Balance</TableHead>
            {isAdmin && <TableHead>Notes</TableHead>}
            <TableHead className="text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {sortedVisible.map((transaction) => {
            const divider = dividerByAnchorId[transaction.id];
            // Total columns: checkbox + type + date + amount + reference + running + (notes if admin) + actions
            const colSpan = isAdmin ? 8 : 7;
            const dividerRow = divider ? (
              <TableRow key={`divider-${transaction.id}`} className={
                divider.tone === 'amber'
                  ? 'bg-gradient-to-r from-amber-50 via-red-50 to-amber-50 border-y border-amber-300 hover:bg-amber-50'
                  : 'bg-gradient-to-r from-blue-50 via-indigo-50 to-blue-50 border-y border-blue-300 hover:bg-blue-50'
              }>
                <TableCell colSpan={colSpan} className="py-2.5 px-4">
                  <div className={`flex items-center gap-3 text-xs font-semibold uppercase tracking-wider ${
                    divider.tone === 'amber' ? 'text-amber-800' : 'text-blue-800'
                  }`} data-testid={`timeline-divider-${divider.tone}`}>
                    <div className={`flex-1 h-px ${divider.tone === 'amber' ? 'bg-amber-300' : 'bg-blue-300'}`} />
                    <span className="px-2 py-1 rounded-full bg-white shadow-sm border border-current/20">
                      {divider.label}
                    </span>
                    <div className={`flex-1 h-px ${divider.tone === 'amber' ? 'bg-amber-300' : 'bg-blue-300'}`} />
                  </div>
                </TableCell>
              </TableRow>
            ) : null;

            const isAchReturn = transaction.type === 'ACH_RETURN';
            const isBounceFee = transaction.type === 'BOUNCE_FEE';
            const isPaymentType = transaction.type === 'PAYMENT';
            const isLawyerCollection = transaction.type === 'LAWYER_COLLECTION';
            const isLawyerFee = transaction.type === 'LAWYER_FEE';
            const isFeeDiscount = transaction.type === 'FEE_DISCOUNT';
            const isManualAdjustment = transaction.type === 'ADJUSTMENT_INCREASE';
            const isNegativeManualAdj = isManualAdjustment && transaction.amount < 0;
            const isRefund = transaction.type === 'REFUND';
            const isDefaultFee = transaction.type === 'DEFAULT_FEE';
            const isDeferredFee = transaction.type === 'DEFERRED_FEE';
            const isDiscount = transaction.type === 'DISCOUNT';
            const isRefinancePayoff = transaction.type === 'REFINANCE_PAYOFF';
            const isEarlyPayoff = transaction.type === 'EARLY_PAYOFF';
            const isNegative = isAchReturn || isBounceFee || isDefaultFee || isDeferredFee || isRefund;
            const isRefundable = (isPaymentType || isLawyerCollection) && !transaction.metadata?.refunded;
            const linkedFee = isLawyerCollection ? transaction.metadata?.contingency_fee : null;
            // Visual grouping cue: a BOUNCE_FEE that's tied to a specific bounce
            // event (via metadata) gets the same red wash as its sibling
            // ACH_RETURN row, so the operator scans Payment → Bounce → Fee as
            // a single visual block.
            const isBounceLinkedFee = isBounceFee && (
              transaction.metadata?.bounced_transaction_id ||
              transaction.metadata?.ach_return_id ||
              transaction.related_ach_return_id
            );

            return (
              <React.Fragment key={transaction.id}>
                {dividerRow}
                <TableRow
                  className={`cursor-pointer hover:bg-gray-50 ${isAchReturn || isBounceLinkedFee ? 'bg-red-50' : ''} ${isLawyerFee ? 'bg-slate-50/60' : ''}`}
                  onClick={() => onEditTransaction(transaction)}
                >
                <TableCell onClick={(e) => e.stopPropagation()}>
                  <input
                    type="checkbox"
                    checked={selectedTransactions.includes(transaction.id)}
                    onMouseDown={(e) => { shiftHeldRef.current = e.shiftKey; }}
                    onChange={() => handleCheckboxChange(transaction.id, visibleIds)}
                    className="h-4 w-4 cursor-pointer"
                    data-testid={`tx-checkbox-${transaction.id}`}
                  />
                </TableCell>
                <TableCell onClick={(e) => e.stopPropagation()}>
                  {isAchReturn ? (
                    <Badge variant="outline" className="text-red-700 border-red-400 bg-red-100 font-semibold" data-testid={`ach-return-badge-${transaction.id}`}>ACH Return</Badge>
                  ) : isBounceFee ? (
                    <Badge variant="outline" className="text-orange-700 border-orange-400 bg-orange-100 font-semibold" data-testid={`bounce-fee-badge-${transaction.id}`}>Bounce Fee</Badge>
                  ) : isDefaultFee ? (
                    <Badge variant="outline" className="text-red-600 border-red-300">Default Fee</Badge>
                  ) : isDeferredFee ? (
                    <Badge variant="outline" className="text-amber-700 border-amber-400 bg-amber-50 font-semibold" data-testid={`deferred-fee-badge-${transaction.id}`}>Deferred Fee</Badge>
                  ) : isLawyerCollection ? (
                    <div className="flex items-center gap-1.5">
                      <Badge variant="outline" className="text-purple-700 border-purple-400 bg-purple-50 font-semibold">Lawyer Collection</Badge>
                      {linkedFee > 0 && <span className="text-[10px] text-orange-600 font-medium">(fee: {formatCurrency(linkedFee)})</span>}
                    </div>
                  ) : isLawyerFee ? (
                    <Badge variant="outline" className="text-slate-500 border-slate-300 bg-slate-50 text-xs">Lawyer Fee (Info)</Badge>
                  ) : isPaymentType ? (
                    bounceConfirmId === transaction.id ? (
                      <div className="flex items-center gap-1">
                        <button onClick={() => { onMarkBounced && onMarkBounced(transaction.id); setBounceConfirmId(null); }} className="px-2 py-0.5 text-xs font-semibold text-white bg-red-600 rounded hover:bg-red-700 transition-colors" data-testid={`confirm-bounce-${transaction.id}`}>Confirm</button>
                        <button onClick={() => setBounceConfirmId(null)} className="px-2 py-0.5 text-xs text-gray-500 rounded hover:bg-gray-100">Cancel</button>
                      </div>
                    ) : (
                      <Badge variant="outline" className="cursor-pointer hover:bg-red-50 hover:text-red-600 hover:border-red-300 transition-colors" onClick={() => setBounceConfirmId(transaction.id)} title="Click to mark as bounced" data-testid={`payment-badge-${transaction.id}`}>{typeLabel(transaction.type)}</Badge>
                    )
                  ) : isFeeDiscount ? (
                    <Badge variant="outline" className="text-emerald-700 border-emerald-400 bg-emerald-50">Fee Discount</Badge>
                  ) : isRefund ? (
                    <Badge variant="outline" className="text-orange-700 border-orange-400 bg-orange-50">Refund</Badge>
                  ) : isManualAdjustment ? (
                    <Badge variant="outline" className={isNegativeManualAdj ? "text-blue-700 border-blue-400 bg-blue-50" : "text-amber-700 border-amber-400 bg-amber-50"}>{isNegativeManualAdj ? 'Credit Adjustment' : 'Manual Adjustment'}</Badge>
                  ) : isDiscount ? (
                    <Badge variant="outline" className="text-teal-700 border-teal-400 bg-teal-50 font-semibold" data-testid={`discount-badge-${transaction.id}`}>Discount</Badge>
                  ) : isRefinancePayoff ? (
                    <Badge variant="outline" className="text-indigo-700 border-indigo-400 bg-indigo-50 font-semibold" data-testid={`refi-badge-${transaction.id}`}>Refinance Payoff</Badge>
                  ) : isEarlyPayoff ? (
                    <Badge variant="outline" className="text-cyan-700 border-cyan-400 bg-cyan-50 font-semibold" data-testid={`early-payoff-badge-${transaction.id}`}>Early Payoff</Badge>
                  ) : (
                    <Badge variant="outline" className={isNegative ? 'text-red-600 border-red-300' : ''}>{typeLabel(transaction.type)}</Badge>
                  )}
                  <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${transaction.upload_session_id ? 'bg-blue-50 text-blue-600 border border-blue-200' : 'bg-gray-50 text-gray-500 border border-gray-200'}`} data-testid={`source-tag-${transaction.id}`}>
                    {transaction.upload_session_id ? 'Upload' : 'Manual'}
                  </span>
                </TableCell>
                <TableCell className={isAchReturn ? 'text-red-600' : ''}>{formatDate(transaction.date)}</TableCell>
                <TableCell className={
                  isLawyerFee ? 'text-slate-400 text-xs italic' :
                  isLawyerCollection ? 'text-purple-600 font-semibold' :
                  isNegativeManualAdj ? 'text-blue-600 font-semibold' :
                  isFeeDiscount ? 'text-emerald-600 font-semibold' :
                  isDiscount ? 'text-teal-600 font-semibold' :
                  isRefinancePayoff ? 'text-indigo-600 font-semibold' :
                  isEarlyPayoff ? 'text-cyan-600 font-semibold' :
                  isRefund ? 'text-orange-600 font-semibold' :
                  isNegative ? 'text-red-600 font-semibold' : 'text-green-600'
                }>
                  {isNegative && '-'}{isFeeDiscount && '-'}{isNegativeManualAdj && '-'}{isRefund && '-'}{formatCurrency(Math.abs(transaction.amount))}
                  {isLawyerFee && ' (info)'}
                </TableCell>
                <TableCell className="text-sm text-gray-600">{transaction.reference_number || '-'}</TableCell>
                <TableCell className="font-medium">
                  {isLawyerFee ? <span className="text-slate-400 text-xs">—</span> : formatCurrency(transaction.running_balance)}
                </TableCell>
                {isAdmin && (
                  <TableCell className="text-sm text-gray-500 max-w-[220px]">
                    {/* Deferred-fee link: render the source-deal #id as a click-through.
                        Both legacy historical rows and Feb-2026 paired rows surface
                        this when metadata.deferred_from_deal_id_number is present. */}
                    {transaction.type === 'DEFERRED_FEE' && transaction.metadata?.deferred_from_deal_id_number ? (
                      <span className="block">
                        Deferred from{' '}
                        <Link
                          to={`/deals/${transaction.metadata.deferred_from_deal_id_number}`}
                          onClick={(e) => e.stopPropagation()}
                          className="text-blue-700 underline underline-offset-2 hover:text-blue-900 font-medium"
                          data-testid={`deferred-from-link-${transaction.id}`}
                        >
                          #{transaction.metadata.deferred_from_deal_id_number}
                        </Link>
                        {transaction.notes && transaction.notes !== `Deferred from #${transaction.metadata.deferred_from_deal_id_number}` && (
                          <>
                            {' — '}
                            <span title={transaction.notes}>
                              {(transaction.notes || '').replace(`Deferred from #${transaction.metadata.deferred_from_deal_id_number}`, '').replace(/^[\s—-]+/, '').slice(0, 60)}
                            </span>
                          </>
                        )}
                      </span>
                    ) : isAchReturn && pullOfReturn(transaction) ? (
                      <span className="truncate block" title={transaction.notes || ''}>
                        <span className="font-medium text-red-700">Bounced — {pullLabel(pullOfReturn(transaction))}</span>
                        {transaction.notes && <span className="text-gray-500">{` · ${transaction.notes.length > 40 ? `${transaction.notes.substring(0, 40)}...` : transaction.notes}`}</span>}
                      </span>
                    ) : transaction.notes ? (
                      <span className="truncate block" title={transaction.notes}>
                        {transaction.notes.length > 50 ? `${transaction.notes.substring(0, 50)}...` : transaction.notes}
                      </span>
                    ) : <span className="text-gray-300">-</span>}
                  </TableCell>
                )}
                <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                  <div className="flex items-center justify-end gap-1">
                    {isAdmin && isPaymentType && transaction.status !== 'bounced' && !returnedPaymentIds.has(transaction.id) && onBounceTransaction && (
                      <Button size="sm" variant="outline" className="h-7 px-2 text-xs text-red-600 border-red-300 hover:bg-red-50" onClick={() => onBounceTransaction(transaction)} title="Bounce This Payment" data-testid={`bounce-btn-${transaction.id}`}>
                        <AlertCircle className="h-3 w-3 mr-1" />Bounce
                      </Button>
                    )}
                    {isPaymentType && returnByPaymentId.has(transaction.id) && (() => {
                      const r = returnByPaymentId.get(transaction.id)
                        || (transactions || []).find((t) => t.id === transaction.metadata?.ach_return_id && liveReturnIds.has(t.id));
                      const code = (String(r.notes || r.description || '').match(/\bR\d{2}\b/i) || [''])[0].toUpperCase();
                      return (
                        <span className="h-7 inline-flex items-center px-2 text-[11px] rounded border border-red-200 bg-red-50 text-red-700"
                              title="This pull already has a return. To bounce it, re-link that return from its own row first.">
                          bounced {formatDate(r.date)}{code ? ` · ${code}` : ''}
                        </span>
                      );
                    })()}
                    {/* 10/27 (Josh): Re-link hidden — "too detailed"; the route stays, flip SHOW_RELINK to bring it back */}
                    {SHOW_RELINK && isAdmin && isAchReturn && transaction.status !== 'void' && onRelinkReturn && (
                      relinkFor === transaction.id ? (
                        <span className="inline-flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                          <select className="h-7 text-xs border rounded px-1" value={relinkTo} onChange={(e) => setRelinkTo(e.target.value)}>
                            <option value="">Move to pull…</option>
                            {relinkCandidates(transaction).map((p) => (
                              <option key={p.id} value={p.id}>{formatDate(p.date)} · {formatCurrency(Math.abs(p.amount))} · {p.reference_number || p.reference || 'no ref'}</option>
                            ))}
                          </select>
                          <Button size="sm" className="h-7 px-2 text-xs" disabled={!relinkTo}
                                  onClick={async () => { await onRelinkReturn(transaction.id, relinkTo); setRelinkFor(null); setRelinkTo(''); }}>Move</Button>
                          <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" onClick={() => { setRelinkFor(null); setRelinkTo(''); }}>Cancel</Button>
                        </span>
                      ) : (
                        <Button size="sm" variant="outline" className="h-7 px-2 text-xs text-slate-600 border-slate-300 hover:bg-slate-50"
                                onClick={() => { setRelinkFor(transaction.id); setRelinkTo(''); }}
                                title="Move this return onto the pull it actually reversed" data-testid={`relink-btn-${transaction.id}`}>
                          Re-link
                        </Button>
                      )
                    )}
                    {isAdmin && isRefundable && onRefundTransaction && (
                      <Button size="sm" variant="outline" className="h-7 px-2 text-xs text-orange-600 border-orange-300 hover:bg-orange-50" onClick={() => onRefundTransaction(transaction.id)} title="Refund This Payment" data-testid={`refund-btn-${transaction.id}`}>
                        <Undo2 className="h-3 w-3 mr-1" />Refund
                      </Button>
                    )}
                    {isAdmin && isDeferredFee && transaction.metadata?.linked_offset_transaction_id && transaction.status !== 'void' && onReverseDefer && (
                      <Button size="sm" variant="outline" className="h-7 px-2 text-xs text-amber-700 border-amber-300 hover:bg-amber-50" onClick={() => onReverseDefer(transaction)} title="Reverse Deferred Fee (voids both legs)" data-testid={`reverse-defer-btn-${transaction.id}`}>
                        <RotateCcw className="h-3 w-3 mr-1" />Reverse
                      </Button>
                    )}
                    {onDeleteTransaction && (
                    <Button size="sm" variant="destructive" onClick={() => onDeleteTransaction(transaction.id)}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                    )}
                  </div>
                </TableCell>
                </TableRow>
              </React.Fragment>
            );
          })}
          {/* Trailing standalone dividers — e.g. "Default declared" event whose
              date is later than every transaction in the list. */}
          {standaloneDividers.map((divider) => {
            const colSpan = isAdmin ? 8 : 7;
            return (
              <TableRow key={`standalone-divider-${divider.key}`} className={
                divider.tone === 'amber'
                  ? 'bg-gradient-to-r from-amber-50 via-red-50 to-amber-50 border-y border-amber-300 hover:bg-amber-50'
                  : 'bg-gradient-to-r from-blue-50 via-indigo-50 to-blue-50 border-y border-blue-300 hover:bg-blue-50'
              }>
                <TableCell colSpan={colSpan} className="py-2.5 px-4">
                  <div className={`flex items-center gap-3 text-xs font-semibold uppercase tracking-wider ${
                    divider.tone === 'amber' ? 'text-amber-800' : 'text-blue-800'
                  }`} data-testid={`timeline-divider-${divider.tone}`}>
                    <div className={`flex-1 h-px ${divider.tone === 'amber' ? 'bg-amber-300' : 'bg-blue-300'}`} />
                    <span className="px-2 py-1 rounded-full bg-white shadow-sm border border-current/20">
                      {divider.label}
                    </span>
                    <div className={`flex-1 h-px ${divider.tone === 'amber' ? 'bg-amber-300' : 'bg-blue-300'}`} />
                  </div>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
};

export default TransactionList;
